import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
const FontSizeContext = createContext();

export const FontSizeProvider = ({ children }) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const DEFAULT_FONT_SIZES = {
    header: "16px",
    subListLink: "1.8rem",
    subListSize: "1.8rem",
    navTabs: "1.4rem",
    searchmovieText: "1.5rem",
    copyrightLink: "1.3rem",
    headerNavbar: screenWidth <= 740 ? "1.3rem" : "1.6rem",
    sessionLogos: "1.6rem",
    TitleWithPoster: screenWidth <= 576 ? "1.6rem" : screenWidth <= 992 ? "2rem" : "2.4rem",
    TitleWithPosterDuration: "24px",
    privacyPolicy: screenWidth <= 740 ? "2rem" : "2.4rem",
    bonusFaqItem: "18px",
    subtitleLineHeight: "20px",
    BonusInfoWrapper: "20px",
    rewarditemLineHeight: "16px",
    descriptionPart: "22px",
    descriptionPartLineHeight: "22px",
    contentpartLineHeight: "30px",
    bonusdetailLineHeight: "24px",
    competitiondetailLineHeight: "24px",
    eventdetailLineHeight: "26px",
    descriptionCompetitionLineHeight: "34px",
    bonuscardDescriptionLineheight: "23px",
    technologyItemHeight: "130px",
    bonusRewardsItemDescription: "12px",
    descriptionText: "14px",
    toggleExpandBtn: "15px",
    competitionHeight: "120px",
    seatlegend: "1.2rem",
    withChevronPadding: "16px",
  };

  const [fontSize, setFontSize] = useState(() => {
    const storedFontSize = localStorage.getItem('fontSize');
    return storedFontSize ? JSON.parse(storedFontSize) : DEFAULT_FONT_SIZES;
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);




  const MIN_FONT_SIZE = 0.8; // Min in rem

  const MAX_FONT_SIZES = useMemo(() => {
    return {
      header: screenWidth > 768 ? "32px" : "20px",
      subListLink: screenWidth > 768 ? "3.2rem" : "2rem",
      subListSize: "1.8rem",
      navTabs: screenWidth <= 375 ? "1.5rem" : screenWidth <= 425 ? "1.6rem" : screenWidth <= 768 ? "1.8rem" : "2.8rem",
      copyrightLink: screenWidth <= 768 ? "2rem" : "2.6rem",
      headerNavbar: screenWidth <= 425 ? "1.8rem" : screenWidth >= 1708 ? "2.6rem" : screenWidth >= 1468 ? "2rem" : "1.8rem",
      TitleWithPoster: screenWidth <= 576 ? "1.8rem" : screenWidth <= 992 ? "2.5rem" : "3.4rem",
      TitleWithPosterDuration: screenWidth <= 992 ? "30px" : "34px",
      bonusFaqItem: screenWidth <= 768 ? "24px" : screenWidth <= 992 ? "30px" : "36px",
      BonusInfoWrapper: screenWidth <= 992 ? "25px" : "35px",
      descriptionPart: screenWidth <= 992 ? "30px" : "40px",
      descriptionPartLineHeight: "40px",
      bonusdetailLineHeight: screenWidth <= 992 ? "20px" : "40px",
      competitiondetailLineHeight: "40px",
      eventdetailLineHeight: "35px",
      sessionLogos: screenWidth <= 1200 ? "2rem" : "2.8rem",
      competitionHeight: "170px",
      descriptionCompetitionLineHeight: "50px",
      contentpartLineHeight: "35px",
      bonuscardDescriptionLineheight: "46px",
      descriptionText: screenWidth <= 992 ? "20px" : "28px",
      toggleExpandBtn: screenWidth <= 992 ? "20px" : "30px",
      technologyItemHeight: "260px",
      bonusRewardsItemDescription: screenWidth <= 992 ? "20px" : "24px",
      seatlegend: "2.4rem",
      subtitleLineHeight: "40px",
      rewarditemLineHeight: "32px",
      withChevronPadding: screenWidth <= 992 ? "16px" : "32px",
      privacyPolicy: screenWidth <= 740 ? "2.4rem" : "3.4rem",
      searchmovieText: "3rem"
    };
  }, [screenWidth]);



  useEffect(() => {
    setFontSize((prevSizes) => {
      const newSizes = { ...prevSizes };
      Object.keys(newSizes).forEach((key) => {
        if (parseFloat(newSizes[key]) > parseFloat(MAX_FONT_SIZES[key])) {
          newSizes[key] = MAX_FONT_SIZES[key]; // Reset to new max
        }
      });
      return newSizes;
    });
  }, [screenWidth]);

  useEffect(() => {
    Object.keys(fontSize).forEach((key) => {
      document.documentElement.style.setProperty(`--${key}-font-size`, fontSize[key]);
    });
    localStorage.setItem("fontSize", JSON.stringify(fontSize));

  }, [fontSize]);


  const extractSizeAndUnit = (value) => {
    const match = value.match(/([\d.]+)([a-zA-Z%]+)/);
    return match ? [parseFloat(match[1]), match[2]] : [null, null];
  };

  const updateFontSize = (newSizes) => {
    setFontSize(newSizes);
  };


  const increaseFontSize = () => {
    const newFontSize = { ...fontSize };

    Object.keys(fontSize).forEach((key) => {
      const [size, unit] = extractSizeAndUnit(fontSize[key]);
      const maxSize = parseFloat(MAX_FONT_SIZES[key]);
      let newSize = size + (unit === 'rem' ? 0.2 : 2);

      if (newSize > maxSize) {
        newSize = maxSize;
      }

      newFontSize[key] = `${newSize}${unit}`;
    });

    updateFontSize(newFontSize);
  };

  const decreaseFontSize = () => {
    const newFontSize = { ...fontSize };

    Object.keys(fontSize).forEach((key) => {
      const [size, unit] = extractSizeAndUnit(fontSize[key]);
      const minSize = MIN_FONT_SIZE;
      let newSize = size - (unit === 'rem' ? 0.2 : 2);

      if (newSize < minSize) {
        newSize = minSize;
      }

      newFontSize[key] = `${newSize}${unit}`;
    });

    updateFontSize(newFontSize);
  };

  const resetFontSize = () => {
    setFontSize(DEFAULT_FONT_SIZES);
  };

  return (
    <FontSizeContext.Provider value={{ fontSize, increaseFontSize, decreaseFontSize, resetFontSize }}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => useContext(FontSizeContext);



